import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { AnalyticsResponse } from '../../../../shared/interfaces/analytics.interface';
import { formatCompactCurrency, formatCurrency, formatNumber, formatRange, formatTrend } from '../../utils/report-format';
import { buildHeadline, buildInsightItems } from '../../utils/report-narrative';

interface KpiCell {
  label: string;
  value: string;
  trend: number | null;
  previous: string;
  caption: string;
}

@Component({
  selector: 'app-analytics-summary',
  standalone: true,
  templateUrl: './analytics-summary.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnalyticsSummary {
  private readonly dataSig = signal<AnalyticsResponse | null>(null);

  @Input() set data(value: AnalyticsResponse | null) {
    this.dataSig.set(value);
  }
  @Input() loading = false;
  @Input() rangeLabel = '';
  /** Abre el panel con el resumen completo */
  @Output() openSummary = new EventEmitter<void>();

  readonly formatTrend = formatTrend;

  readonly previousRangeLabel = computed(() => {
    const c = this.dataSig()?.comparison;
    return c ? formatRange(new Date(c.previousStartDate), new Date(c.previousEndDate)) : '';
  });

  readonly headline = computed(() => {
    const d = this.dataSig();
    return d ? buildHeadline(d) : '';
  });

  readonly insights = computed(() => {
    const d = this.dataSig();
    return d ? buildInsightItems(d) : [];
  });

  readonly kpis = computed<KpiCell[]>(() => {
    const d = this.dataSig();
    if (!d) return [];
    const { kpis, comparison } = d;
    return [
      {
        label: 'Ingresos cobrados',
        value: formatCurrency(kpis.totalRevenue),
        trend: comparison.revenueTrend,
        previous: formatCompactCurrency(comparison.previousRevenue),
        caption: `${formatNumber(kpis.paymentsCount)} ${kpis.paymentsCount === 1 ? 'pago recibido' : 'pagos recibidos'}`,
      },
      {
        label: 'Citas agendadas',
        value: formatNumber(kpis.totalAppointments),
        trend: comparison.appointmentsTrend,
        previous: formatNumber(comparison.previousAppointments),
        caption: `${formatNumber(kpis.completedAppointments)} completadas`,
      },
      {
        label: 'Ticket promedio',
        value: formatCurrency(kpis.averageTicket),
        trend: comparison.averageTicketTrend,
        previous: formatCompactCurrency(comparison.previousAverageTicket),
        caption: 'Promedio por pago',
      },
      {
        label: 'Clientes nuevos',
        value: formatNumber(kpis.newClients),
        trend: comparison.newClientsTrend,
        previous: formatNumber(comparison.previousNewClients),
        caption: `${formatNumber(kpis.returningClients)} ya habían venido antes`,
      },
    ];
  });
}
